import { User } from '../types';
import { logger } from './loggingService';
import { GUEST_ACCOUNT_ID } from './offlineStorageService';

const ACCOUNTS_STORAGE_KEY = 'cc_accounts';
const ACTIVE_ACCOUNT_KEY = 'cc_active_account';

export interface StoredAccount {
  id: string;
  user: User | null;
  isGuest: boolean;
  lastActive: number;
}

export const GUEST_ACCOUNT: StoredAccount = {
  id: GUEST_ACCOUNT_ID,
  user: null,
  isGuest: true,
  lastActive: Date.now()
};

class AccountService {
  private accounts: Map<string, StoredAccount> = new Map();
  private activeAccountId: string = GUEST_ACCOUNT_ID;
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const storedAccounts = localStorage.getItem(ACCOUNTS_STORAGE_KEY);
      if (storedAccounts) {
        const parsed: StoredAccount[] = JSON.parse(storedAccounts);
        this.accounts.clear();
        for (const account of parsed) {
          this.accounts.set(account.id, account);
        }
      }
      
      if (!this.accounts.has(GUEST_ACCOUNT_ID)) {
        this.accounts.set(GUEST_ACCOUNT_ID, { ...GUEST_ACCOUNT });
      }

      const storedActive = localStorage.getItem(ACTIVE_ACCOUNT_KEY);
      if (storedActive && this.accounts.has(storedActive)) {
        this.activeAccountId = storedActive;
      } else {
        this.activeAccountId = GUEST_ACCOUNT_ID;
      }

      logger.debug('System', 'Loaded accounts from storage', { 
        count: this.accounts.size, 
        activeAccountId: this.activeAccountId 
      });
    } catch (e) {
      logger.warn('System', 'Failed to load accounts from storage', { error: (e as any)?.message });
      this.accounts.set(GUEST_ACCOUNT_ID, { ...GUEST_ACCOUNT });
      this.activeAccountId = GUEST_ACCOUNT_ID;
    }
  }

  private saveToStorage() {
    try {
      const accountsArray = Array.from(this.accounts.values());
      localStorage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(accountsArray));
      localStorage.setItem(ACTIVE_ACCOUNT_KEY, this.activeAccountId);
    } catch (e) {
      logger.warn('System', 'Failed to save accounts to storage', { error: (e as any)?.message });
    }
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getActiveAccountId(): string {
    return this.activeAccountId;
  }

  getActiveAccount(): StoredAccount | null {
    return this.accounts.get(this.activeAccountId) || null;
  }

  isGuestMode(): boolean {
    return this.activeAccountId === GUEST_ACCOUNT_ID;
  }

  getAllAccounts(): StoredAccount[] {
    return Array.from(this.accounts.values());
  }

  getGoogleAccounts(): StoredAccount[] {
    return Array.from(this.accounts.values()).filter(a => !a.isGuest);
  }

  addAccount(user: User): StoredAccount {
    const existing = this.accounts.get(user.id);
    if (existing) {
      existing.user = user;
      existing.lastActive = Date.now();
      this.saveToStorage();
      this.notifyListeners();
      logger.info('System', 'Updated existing account', { id: user.id, username: user.username });
      return existing;
    }

    const newAccount: StoredAccount = {
      id: user.id,
      user,
      isGuest: false,
      lastActive: Date.now()
    };
    this.accounts.set(user.id, newAccount);
    this.saveToStorage();
    this.notifyListeners();
    logger.info('System', 'Added new account', { id: user.id, username: user.username });
    return newAccount;
  }

  removeAccount(accountId: string): boolean {
    if (accountId === GUEST_ACCOUNT_ID) {
      logger.warn('System', 'Cannot remove guest account');
      return false;
    }

    const removed = this.accounts.delete(accountId);
    if (removed) {
      if (this.activeAccountId === accountId) {
        this.activeAccountId = GUEST_ACCOUNT_ID;
      }
      this.saveToStorage();
      this.notifyListeners();
      logger.info('System', 'Removed account', { id: accountId });
    }
    return removed;
  }

  setActiveAccount(accountId: string): boolean {
    if (!this.accounts.has(accountId)) {
      logger.warn('System', 'Cannot set active account - not found', { id: accountId });
      return false;
    }

    const account = this.accounts.get(accountId);
    if (account) {
      account.lastActive = Date.now();
    }

    this.activeAccountId = accountId;
    this.saveToStorage();
    this.notifyListeners();
    logger.info('System', 'Set active account', { id: accountId });
    return true;
  }

  switchToGuest(): void {
    this.setActiveAccount(GUEST_ACCOUNT_ID);
  }

  getAccountById(accountId: string): StoredAccount | null {
    return this.accounts.get(accountId) || null;
  }

  hasAccount(accountId: string): boolean {
    return this.accounts.has(accountId);
  }

  getGuestProjectCount(): number {
    return 0;
  }

  setGuestProjectCount(count: number): void {
  }

  clear(): void {
    this.accounts.clear();
    this.accounts.set(GUEST_ACCOUNT_ID, { ...GUEST_ACCOUNT });
    this.activeAccountId = GUEST_ACCOUNT_ID;
    this.saveToStorage();
    this.notifyListeners();
    logger.info('System', 'Cleared all accounts');
  }
}

export const accountService = new AccountService();
